import { useEffect, useState } from 'react';
import {
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  Clock3,
  Plus,
  QrCode,
  Users
} from 'lucide-react';
import { useNavigate } from '../../app/router';
import { readToken, tenantTokenKey } from '../../app/session';
import { useTenantApp } from '../app/TenantAppContext';
import { useTranslation } from '../../i18n/i18n';
import { ApiError, apiRequest } from '../../lib/api';

interface DashboardData {
  summary: {
    activeUsers: number;
    present: number;
    absent: number;
    overdueTasks: number;
    pendingLeave: number;
  };
  myTasks: Array<{
    id: string;
    number: number;
    title: string;
    status: string;
    priority: string;
    dueAt: string | null;
  }>;
  presence: Array<{
    id: string;
    firstName: string;
    lastName: string;
    jobTitle: string | null;
    startedAt: string;
  }>;
}

const emptyDashboard: DashboardData = {
  summary: {
    activeUsers: 0,
    present: 0,
    absent: 0,
    overdueTasks: 0,
    pendingLeave: 0
  },
  myTasks: [],
  presence: []
};

export function DashboardPage() {
  const navigate = useNavigate();
  const { context, modules } = useTenantApp();
  const { locale, t } = useTranslation();
  const [dashboard, setDashboard] = useState(emptyDashboard);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiRequest<DashboardData>('/api/v1/dashboard', {
      accessToken: readToken(tenantTokenKey)
    })
      .then(setDashboard)
      .catch((caught) =>
        setError(
          caught instanceof ApiError
            ? caught.message
            : 'Dashboard se trenutačno ne može učitati.'
        )
      )
      .finally(() => setLoading(false));
  }, []);

  const today = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  }).format(new Date());
  const formatTime = (value: string) =>
    new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(value));
  const hour = new Date().getHours();
  const greeting =
    hour < 11
      ? t('dashboard.greeting.morning')
      : hour < 18
        ? t('dashboard.greeting.day')
        : t('dashboard.greeting.evening');

  if (loading) return <div className="page-loader"><span /></div>;

  return (
    <div className="dashboard-content">
      {error && <div className="form-error">{error}</div>}
      <section className="dashboard-welcome">
        <div>
          <span className="eyebrow">{today}</span>
          <h1>{greeting}, {context.user.firstName}.</h1>
          <p>{t('dashboard.subtitle')}</p>
        </div>
        <div className="welcome-actions">
          {modules.has('attendance') && (
            <button className="button button--qr" onClick={() => navigate('/app/attendance')}>
              <QrCode size={19} /> {t('nav.attendance')}
            </button>
          )}
          {modules.has('tasks') && context.user.permissions.includes('tasks.create') && (
            <button className="button button--primary" onClick={() => navigate('/app/tasks?new=1')}>
              <Plus size={19} /> {t('action.newTask')}
            </button>
          )}
        </div>
      </section>

      <section className="metric-grid">
        {modules.has('attendance') && (
          <article className="metric-card metric-card--green">
            <span className="metric-icon"><Users size={20} /></span>
            <div>
              <small>{t('dashboard.present')}</small>
              <strong>{dashboard.summary.present}<em> / {dashboard.summary.activeUsers}</em></strong>
            </div>
            <span className="metric-trend">{t('common.live')}</span>
          </article>
        )}
        {modules.has('tasks') && (
          <article className="metric-card metric-card--blue">
            <span className="metric-icon"><CheckCircle2 size={20} /></span>
            <div><small>{t('dashboard.myTasks')}</small><strong>{dashboard.myTasks.length}</strong></div>
            <span className="metric-trend">{t('common.today')}</span>
          </article>
        )}
        {modules.has('tasks') && (
          <article className="metric-card metric-card--orange">
            <span className="metric-icon"><CircleAlert size={20} /></span>
            <div><small>{t('dashboard.overdue')}</small><strong>{dashboard.summary.overdueTasks}</strong></div>
          </article>
        )}
        {modules.has('leave') && context.user.dataScope !== 'SELF' && (
          <article className="metric-card metric-card--purple">
            <span className="metric-icon"><CalendarDays size={20} /></span>
            <div><small>{t('dashboard.pendingLeave')}</small><strong>{dashboard.summary.pendingLeave}</strong></div>
          </article>
        )}
      </section>

      <section className="dashboard-grid">
        {modules.has('tasks') && (
          <article className="dashboard-panel tasks-panel">
            <header>
              <div><h2>{t('dashboard.myTasks')}</h2><p>{t('dashboard.focus')}</p></div>
              <button className="text-button" onClick={() => navigate('/app/tasks')}>
                {t('dashboard.all')} <ChevronRight size={16} />
              </button>
            </header>
            <div className="task-list">
              {dashboard.myTasks.map((task) => (
                <button
                  className="task-item"
                  key={task.id}
                  onClick={() => navigate(`/app/tasks?task=${task.id}`)}
                >
                  <span className={`priority priority--${task.priority.toLowerCase()}`} />
                  <span className="task-item__body">
                    <strong>{task.title}</strong>
                    <small>
                      #{task.number} · {task.dueAt
                        ? `${t('tasks.due').toLowerCase()} ${formatTime(task.dueAt)}`
                        : t('common.noDeadline')}
                    </small>
                  </span>
                  <span className="task-state">{task.status.replaceAll('_', ' ')}</span>
                  <ChevronRight size={18} />
                </button>
              ))}
              {!dashboard.myTasks.length && (
                <div className="empty-state">
                  <span><CheckCircle2 /></span>
                  <strong>{t('dashboard.emptyTasks')}</strong>
                  <p>{t('dashboard.emptyTasksBody')}</p>
                </div>
              )}
            </div>
          </article>
        )}

        {modules.has('attendance') && (
          <article className="dashboard-panel presence-panel">
            <header>
              <div><h2>{t('dashboard.whoWorks')}</h2><p>{t('dashboard.livePresence')}</p></div>
              <span className="live-badge"><i /> {t('common.live')}</span>
            </header>
            <div className="presence-list">
              {dashboard.presence.map((person) => (
                <div className="presence-item" key={person.id}>
                  <span className="avatar avatar--soft">
                    {person.firstName[0]}{person.lastName[0]}
                  </span>
                  <span>
                    <strong>{person.firstName} {person.lastName}</strong>
                    <small>{person.jobTitle ?? 'Zaposlenik'}</small>
                  </span>
                  <time><i /> {formatTime(person.startedAt)}</time>
                </div>
              ))}
              {!dashboard.presence.length && (
                <div className="empty-state empty-state--small">
                  <Clock3 /><p>{t('dashboard.emptyPresence')}</p>
                </div>
              )}
            </div>
          </article>
        )}
      </section>
    </div>
  );
}
